import React, { useState, useEffect, useRef, useCallback } from "react"
import { cn } from "@/lib/utils"
import { AcademicianButton } from "@/components/academician/academician-button"
import { CheckIcon, ArrowRightIcon } from "lucide-react"
import { apiPatch, apiPost } from "@/lib/api"

declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId?: string
          playerVars?: Record<string, unknown>
          events?: {
            onReady?: (event: { target: YTPlayer }) => void
            onStateChange?: (event: { data: number }) => void
            onPlaybackRateChange?: (event: { data: number; target: YTPlayer }) => void
          }
        }
      ) => YTPlayer
      PlayerState: {
        PLAYING: number
        PAUSED: number
        BUFFERING: number
        ENDED: number
        CUED: number
      }
    }
    onYouTubeIframeAPIReady?: () => void
    Vimeo: {
      Player: new (element: HTMLIFrameElement) => VimeoPlayer
    }
  }
}

interface YTPlayer {
  getCurrentTime: () => number
  getDuration: () => number
  destroy: () => void
}

interface VimeoPlayer {
  getCurrentTime: () => Promise<number>
  getDuration: () => Promise<number>
  on: (event: string, callback: (data?: Record<string, unknown>) => void) => void
  off: (event: string) => void
  destroy: () => void
}

interface TrackEvent {
  type: string
  at?: number
  ts: number
  from?: number
  to?: number
  rate?: number
}

export interface VideoPlayerProps {
  title: string
  videoUrl?: string
  watchPercentage?: number
  isCompleted?: boolean
  onMarkComplete?: () => void
  onProgressChange?: (percentage: number) => void
  onTimeUpdate?: (seconds: number) => void
  videoPlatform?: string
  initialSeconds?: number
  updateProgressUrl?: string
  trackEventUrl?: string
  nextContentUrl?: string
  nextContentType?: string
  className?: string
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement("script")
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.head.appendChild(script)
  })
}

/** Extract YouTube video ID and start time from an embed URL */
function parseYouTubeUrl(url: string): { videoId: string; start: number } {
  // URL like: https://www.youtube.com/embed/4GQK24_PWqA?start=270
  const match = url.match(/\/embed\/([^?/]+)/)
  const videoId = match?.[1] || ""

  let start = 0
  try {
    const urlObj = new URL(url)
    const startParam = urlObj.searchParams.get("start")
    if (startParam) start = parseInt(startParam, 10)
  } catch { /* ignore parse errors */ }

  return { videoId, start }
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  title,
  videoUrl,
  watchPercentage = 0,
  isCompleted = false,
  onMarkComplete,
  onProgressChange,
  onTimeUpdate,
  videoPlatform,
  initialSeconds = 0,
  updateProgressUrl,
  trackEventUrl,
  nextContentUrl,
  nextContentType,
  className
}) => {
  const [currentProgress, setCurrentProgress] = useState(watchPercentage)
  const showCompleteButton = true
  const lastSavedSeconds = useRef(initialSeconds)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const ytPlayerRef = useRef<YTPlayer | null>(null)
  const vimeoPlayerRef = useRef<VimeoPlayer | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const eventBufferRef = useRef<TrackEvent[]>([])
  const videoDurationRef = useRef<number>(0)
  const lastKnownTimeRef = useRef<number>(initialSeconds)
  const lastRealTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    setCurrentProgress(watchPercentage)
  }, [watchPercentage])

  const nowTs = () => Math.floor(Date.now() / 1000)

  const pushEvent = useCallback((event: TrackEvent) => {
    eventBufferRef.current.push(event)
  }, [])

  const flushEvents = useCallback(async () => {
    if (!trackEventUrl || eventBufferRef.current.length === 0) return

    const events = [...eventBufferRef.current]
    eventBufferRef.current = []

    try {
      await apiPost(trackEventUrl, { events })
    } catch {
      // Re-add events on failure so they can be retried
      eventBufferRef.current = [...events, ...eventBufferRef.current]
    }
  }, [trackEventUrl])

  const saveProgress = useCallback(
    async (seconds: number) => {
      if (!updateProgressUrl) return
      if (Math.abs(seconds - lastSavedSeconds.current) < 2) return

      lastSavedSeconds.current = seconds
      try {
        const body: Record<string, number> = {
          progress_seconds: Math.floor(seconds)
        }
        if (videoDurationRef.current > 0) {
          body.video_duration = Math.floor(videoDurationRef.current)
        }
        const response = await apiPatch(updateProgressUrl, body)
        const data = await response.json()
        if (data.progress_percentage != null) {
          setCurrentProgress(data.progress_percentage)
          onProgressChange?.(data.progress_percentage)
        }
      } catch {
        // Silently fail — will retry on next poll
      }
    },
    [updateProgressUrl, onProgressChange]
  )

  // Flush event buffer every 30 seconds
  useEffect(() => {
    if (!trackEventUrl) return

    flushIntervalRef.current = setInterval(() => {
      flushEvents()
    }, 30000)

    return () => {
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current)
        flushIntervalRef.current = null
      }
    }
  }, [trackEventUrl, flushEvents])

  // YouTube API setup — use div placeholder + YT.Player creates the iframe
  useEffect(() => {
    if (videoPlatform !== "youtube" || !videoUrl) return

    let destroyed = false
    const { videoId, start } = parseYouTubeUrl(videoUrl)

    if (!videoId) return

    const initYouTube = async () => {
      if (!window.YT) {
        await new Promise<void>((resolve) => {
          const prev = window.onYouTubeIframeAPIReady
          window.onYouTubeIframeAPIReady = () => {
            prev?.()
            resolve()
          }
          loadScript("https://www.youtube.com/iframe_api")
        })
      }

      if (destroyed) return

      new window.YT.Player("yt-player-container", {
        videoId,
        playerVars: {
          start: start || undefined,
          enablejsapi: 1,
          origin: window.location.origin,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          showinfo: 0,
          disablekb: 0
        },
        events: {
          onReady: (event) => {
            if (destroyed) return
            ytPlayerRef.current = event.target
            const dur = event.target.getDuration()
            if (dur > 0) videoDurationRef.current = dur
          },
          onStateChange: (event) => {
            if (destroyed || !ytPlayerRef.current) return
            const time = ytPlayerRef.current.getCurrentTime()

            if (event.data === window.YT.PlayerState.PLAYING) {
              const elapsed = (Date.now() - lastRealTimeRef.current) / 1000
              const expectedTime = lastKnownTimeRef.current + elapsed
              if (Math.abs(time - expectedTime) > 3 && lastKnownTimeRef.current > 0) {
                pushEvent({ type: "seek", from: lastKnownTimeRef.current, to: time, ts: nowTs() })
              }
              pushEvent({ type: "play", at: time, ts: nowTs() })
              lastKnownTimeRef.current = time
              lastRealTimeRef.current = Date.now()
            } else if (event.data === window.YT.PlayerState.PAUSED) {
              pushEvent({ type: "pause", at: time, ts: nowTs() })
              lastKnownTimeRef.current = time
              lastRealTimeRef.current = Date.now()
            } else if (event.data === window.YT.PlayerState.BUFFERING) {
              const elapsed = (Date.now() - lastRealTimeRef.current) / 1000
              const expectedTime = lastKnownTimeRef.current + elapsed
              if (Math.abs(time - expectedTime) > 3 && lastKnownTimeRef.current > 0) {
                pushEvent({ type: "seek", from: lastKnownTimeRef.current, to: time, ts: nowTs() })
              }
              pushEvent({ type: "buffer", at: time, ts: nowTs() })
              lastKnownTimeRef.current = time
              lastRealTimeRef.current = Date.now()
            } else if (event.data === window.YT.PlayerState.ENDED) {
              lastKnownTimeRef.current = time
              lastRealTimeRef.current = Date.now()
              saveProgress(time)
            }
          },
          onPlaybackRateChange: (event) => {
            if (destroyed || !ytPlayerRef.current) return
            const time = ytPlayerRef.current.getCurrentTime()
            pushEvent({ type: "speed", rate: event.data, at: time, ts: nowTs() })
          }
        }
      })

      // Poll every 1 second for time update, save progress every 10th tick
      let tickCount = 0
      intervalRef.current = setInterval(() => {
        if (ytPlayerRef.current) {
          const time = ytPlayerRef.current.getCurrentTime()
          if (time > 0) {
            lastKnownTimeRef.current = time
            lastRealTimeRef.current = Date.now()
            onTimeUpdate?.(time)
            tickCount++
            if (tickCount >= 10) {
              tickCount = 0
              saveProgress(time)
            }
          }
        }
      }, 1000)
    }

    initYouTube()

    return () => {
      destroyed = true
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      ytPlayerRef.current = null
    }
  }, [videoPlatform, videoUrl, saveProgress, pushEvent])

  // Vimeo API setup
  useEffect(() => {
    if (videoPlatform !== "vimeo" || !videoUrl) return

    let destroyed = false
    let lastVimeoTime = initialSeconds

    const initVimeo = async () => {
      await loadScript("https://player.vimeo.com/api/player.js")

      if (destroyed || !iframeRef.current) return

      const player = new window.Vimeo.Player(iframeRef.current)
      vimeoPlayerRef.current = player

      try {
        const dur = await player.getDuration()
        if (dur > 0) videoDurationRef.current = dur
      } catch { /* player not ready */ }

      player.on("play", async () => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          pushEvent({ type: "play", at: time, ts: nowTs() })
          lastVimeoTime = time
        } catch { /* player not ready */ }
      })

      player.on("pause", async () => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          pushEvent({ type: "pause", at: time, ts: nowTs() })
          lastVimeoTime = time
        } catch { /* player not ready */ }
      })

      player.on("seeked", async () => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          pushEvent({ type: "seek", from: lastVimeoTime, to: time, ts: nowTs() })
          lastVimeoTime = time
        } catch { /* player not ready */ }
      })

      player.on("bufferstart", async () => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          pushEvent({ type: "buffer", at: time, ts: nowTs() })
        } catch { /* player not ready */ }
      })

      player.on("playbackratechange", async (data) => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          pushEvent({ type: "speed", rate: data?.playbackRate as number, at: time, ts: nowTs() })
        } catch { /* player not ready */ }
      })

      player.on("ended", async () => {
        if (destroyed) return
        try {
          const time = await player.getCurrentTime()
          lastVimeoTime = time
          saveProgress(time)
        } catch { /* player not ready */ }
      })

      // Poll every 10 seconds for progress save
      intervalRef.current = setInterval(async () => {
        if (vimeoPlayerRef.current) {
          try {
            const time = await vimeoPlayerRef.current.getCurrentTime()
            if (time > 0) {
              lastVimeoTime = time
              saveProgress(time)
            }
          } catch {
            // Player may not be ready
          }
        }
      }, 10000)
    }

    initVimeo()

    return () => {
      destroyed = true
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      vimeoPlayerRef.current = null
    }
  }, [videoPlatform, videoUrl, saveProgress, pushEvent, initialSeconds])

  // Flush events and save progress on page unload
  useEffect(() => {
    const handleUnload = () => {
      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || ""

      // Flush remaining analytics events
      if (trackEventUrl && eventBufferRef.current.length > 0) {
        const eventsBody = JSON.stringify({ events: eventBufferRef.current })
        fetch(trackEventUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            Accept: "application/json"
          },
          body: eventsBody,
          keepalive: true,
          credentials: "same-origin"
        }).catch(() => { /* fire and forget */ })
        eventBufferRef.current = []
      }

      // Save progress
      if (!updateProgressUrl) return
      const getCurrentTime = (): number | null => {
        if (ytPlayerRef.current) {
          return ytPlayerRef.current.getCurrentTime()
        }
        return null
      }

      const time = getCurrentTime()
      if (time != null && Math.abs(time - lastSavedSeconds.current) >= 2) {
        const body = JSON.stringify({ progress_seconds: Math.floor(time) })
        fetch(updateProgressUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
            Accept: "application/json"
          },
          body,
          keepalive: true,
          credentials: "same-origin"
        }).catch(() => { /* fire and forget */ })
      }
    }

    window.addEventListener("beforeunload", handleUnload)
    return () => window.removeEventListener("beforeunload", handleUnload)
  }, [updateProgressUrl, trackEventUrl])

  const isYouTube = videoPlatform === "youtube"

  return (
    <div className={cn("space-y-4", className)}>
      {/* Video Player */}
      <div className="aspect-video w-full bg-black rounded-lg overflow-hidden relative">
        {videoUrl ? (
          isYouTube ? (
            // YouTube: render a div — YT.Player creates the iframe
            <>
              <div id="yt-player-container" className="w-full h-full" />
              {/* Transparent overlays to block YouTube branding clicks */}
              <div
                style={{ position: "absolute", top: 0, left: 0, right: 0, height: 60, zIndex: 10, background: "transparent", pointerEvents: "auto" }}
                onContextMenu={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                style={{ position: "absolute", bottom: 0, left: 0, width: 200, height: 50, zIndex: 10, background: "transparent", pointerEvents: "auto" }}
                onContextMenu={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
              />
            </>
          ) : (
            // Vimeo: render an iframe for the Vimeo SDK to wrap
            <iframe
              id="video-iframe"
              ref={iframeRef}
              src={videoUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-primary"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">Video Player</p>
                <p className="text-xs text-muted-foreground">
                  YouTube/Vimeo embed will appear here
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Video Info */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Watch Progress</span>
            <span className="font-medium text-foreground">{Math.round(currentProgress)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300 rounded-full"
              style={{ width: `${currentProgress}%` }}
            />
          </div>
        </div>

        {/* Mark as Complete / Continue */}
        <div className="flex flex-wrap items-center gap-3">
          {showCompleteButton && (
            <AcademicianButton
              variant={isCompleted ? "secondary" : "primary"}
              leftIcon={isCompleted ? <CheckIcon /> : undefined}
              onClick={onMarkComplete}
              disabled={isCompleted}
              className="w-full sm:w-auto"
            >
              {isCompleted ? "Completed" : "Mark as Complete"}
            </AcademicianButton>
          )}
          {isCompleted && nextContentUrl && (
            <a href={nextContentUrl} className="w-full sm:w-auto">
              <AcademicianButton
                variant="primary"
                rightIcon={<ArrowRightIcon />}
                className="w-full"
              >
                Continue to Next {
                  nextContentType === "quiz" ? "Quiz"
                  : nextContentType === "brainstorming_session" ? "Session"
                  : nextContentType === "meetup_session" ? "Meetup"
                  : nextContentType === "activity" ? "Activity"
                  : "Video"
                }
              </AcademicianButton>
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
